import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../utils/toast.js';
import { ContentWrap } from '../components/ContentWrap.js';
import { PageContent } from '../components/PageContent.js';
import { Heading, Text } from '../components/Typography/index.js';
import { useGlobalStore } from '@digiwedge/global-state';
import { useProfile } from '@digiwedge/hooks-auth-web';
import { resolveAvatarUrl, resolveDisplayName } from '../utils/profile.js';
import { getMessage } from '../i18n/messages.js';
import { isAxiosError } from 'axios';
import { MfaSetupFlow } from './MfaSetupFlow.js';
const sanitizeReturnTo = (candidate) => {
    if (!candidate)
        return '/';
    if (!candidate.startsWith('/'))
        return '/';
    return candidate;
};
const formatMfaFactor = (factor) => {
    const normalized = factor.trim().toLowerCase();
    if (normalized === 'whatsapp')
        return 'WhatsApp';
    if (normalized === 'sms')
        return 'Text message';
    if (normalized === 'email')
        return 'Email';
    if (normalized === 'totp' || normalized === 'authenticator')
        return 'Authenticator app';
    if (normalized === 'backup')
        return 'Backup codes';
    return factor
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase() + part.slice(1))
        .join(' ');
};
const normalizeMfaMethod = (factor) => {
    const normalized = factor.trim().toLowerCase();
    if (normalized === 'whatsapp' || normalized === 'sms' || normalized === 'email') {
        return normalized;
    }
    return null;
};
export const MfaSetupPage = () => {
    const [tenantId, setTenantId] = useState(undefined);
    const [verificationComplete, setVerificationComplete] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useGlobalStore((state) => state.profile);
    const mfaEnrolled = useGlobalStore((state) => state.mfaEnrolled);
    const setMfaEnrolled = useGlobalStore((state) => state.setMfaEnrolled);
    const setMfaSessionVerified = useGlobalStore((state) => state.setMfaSessionVerified);
    const setProfile = useGlobalStore((state) => state.setProfile);
    const { profile: idpProfile, fetchProfile } = useProfile();
    const verificationCompletedRef = useRef(false);
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const isManageMode = queryParams.get('mode') === 'manage';
    const profileMfa = idpProfile?.mfa ??
        profile?.mfa;
    const activeMfaFactors = Array.isArray(profileMfa?.factors)
        ? profileMfa.factors.filter((factor) => factor.trim().length > 0)
        : [];
    const enabledMfaMethods = activeMfaFactors
        .map(normalizeMfaMethod)
        .filter((method) => method !== null);
    const defaultMfaMethod = enabledMfaMethods[0] ?? 'whatsapp';
    const hasExistingMfa = Boolean(profileMfa?.enrolled) || mfaEnrolled === true;
    const pendingRedirect = useMemo(() => {
        const stateReturn = location.state?.returnTo;
        const queryReturn = queryParams.get('returnTo');
        return sanitizeReturnTo(stateReturn ?? queryReturn ?? (isManageMode ? '/settings' : '/'));
    }, [isManageMode, location.state, queryParams]);
    const userId = profile?.userId;
    const email = profile?.email ?? '';
    useEffect(() => {
        if (tenantId && userId) {
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const freshProfile = await fetchProfile();
                if (!freshProfile || cancelled) {
                    return;
                }
                const resolvedName = resolveDisplayName(freshProfile);
                const tenantIdFromProfile = freshProfile.tenantIds?.[0];
                if (tenantIdFromProfile) {
                    setTenantId(tenantIdFromProfile);
                }
                if (!userId) {
                    setProfile({
                        userId: freshProfile.userId,
                        name: resolvedName,
                        displayName: freshProfile.displayName ?? resolvedName,
                        email: freshProfile.email,
                        avatarUrl: resolveAvatarUrl(freshProfile.avatar),
                    });
                }
                const hasMfaEnrolled = Boolean(freshProfile.mfa?.enrolled);
                const hasSessionVerification = hasMfaEnrolled
                    ? Boolean(freshProfile.mfa?.lastVerifiedAt)
                    : true;
                if (verificationCompletedRef.current) {
                    return;
                }
                setMfaEnrolled(hasMfaEnrolled);
                setMfaSessionVerified(hasSessionVerification);
            }
            catch (error) {
                if (cancelled)
                    return;
                if (isAxiosError(error) && error.response?.status === 401) {
                    setMfaEnrolled(false);
                    setMfaSessionVerified(false);
                    return;
                }
                toast.error(getMessage('en-US', 'mfaCodeError'));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        tenantId,
        userId,
        fetchProfile,
        setProfile,
        setMfaEnrolled,
        setMfaSessionVerified,
        setTenantId,
    ]);
    const handleMfaVerified = async () => {
        verificationCompletedRef.current = true;
        setMfaEnrolled(true);
        setMfaSessionVerified(true);
        try {
            const refreshedProfile = await fetchProfile();
            if (refreshedProfile) {
                const resolvedName = resolveDisplayName(refreshedProfile);
                const tenantIdFromProfile = refreshedProfile.tenantIds?.[0];
                setTenantId(tenantIdFromProfile);
                setProfile({
                    userId: refreshedProfile.userId,
                    name: resolvedName,
                    displayName: refreshedProfile.displayName ?? resolvedName,
                    email: refreshedProfile.email,
                    avatarUrl: resolveAvatarUrl(refreshedProfile.avatar),
                });
            }
        }
        catch {
            /* ignore profile refresh errors */
        }
        setVerificationComplete(true);
    };
    const handleContinue = () => {
        navigate(pendingRedirect, { replace: true });
    };
    return (_jsxs(PageContent, { className: "relative min-h-screen overflow-hidden bg-gray-50 py-12", children: [_jsx("div", { className: "absolute inset-0 bg-cover bg-center", style: {
                    backgroundImage: "url('https://images.pexels.com/photos/114972/pexels-photo-114972.jpeg')",
                }, "aria-hidden": "true" }), _jsx("div", { className: "absolute inset-0 bg-white/60 backdrop-blur-sm", "aria-hidden": "true" }), _jsx(ContentWrap, { className: "relative max-w-xl mx-auto py-24", children: _jsxs("div", { className: "bg-white/90 backdrop-blur rounded-lg shadow p-8 space-y-6", children: [_jsxs("div", { className: "space-y-2 text-center", children: [_jsx(Heading, { variant: "h4", children: isManageMode ? 'Manage Authentication' : 'Secure Your Account' }), _jsx(Text, { children: isManageMode
                                        ? 'Review your active multi-factor authentication methods and add another factor.'
                                        : 'For your account security, you must set up Multi-Factor Authentication before continuing.' })] }), isManageMode ? (_jsxs("div", { className: "mx-auto w-full max-w-md rounded-md border border-gray-200 bg-white p-4", "data-testid": "mfa-management-summary", children: [_jsx("p", { className: "text-sm font-semibold text-gray-900", children: "Enabled methods" }), activeMfaFactors.length > 0 ? (_jsx("ul", { className: "mt-3 flex flex-wrap gap-2", children: activeMfaFactors.map((factor) => (_jsx("li", { className: "rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800", children: formatMfaFactor(factor) }, factor))) })) : (_jsx("p", { className: "mt-2 text-sm text-gray-600", children: "No active MFA methods are enabled." }))] })) : null, verificationComplete ? (_jsxs("div", { className: "mx-auto w-full max-w-md space-y-4 rounded-md border border-green-200 bg-green-50 p-5 text-center", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("p", { className: "text-lg font-semibold text-green-900", children: "MFA enabled" }), _jsx("p", { className: "text-sm text-green-800", children: "Your WhatsApp verification was accepted and multi-factor authentication is now active on this account." })] }), _jsx("button", { type: "button", className: "inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2", onClick: handleContinue, children: "Continue" })] })) : (_jsx(MfaSetupFlow, { userId: userId, email: email, tenantId: tenantId, alreadyEnrolled: hasExistingMfa, enabledMethods: enabledMfaMethods, defaultMethod: defaultMfaMethod, onVerified: handleMfaVerified })), _jsxs("div", { className: "flex items-center justify-between text-sm text-gray-500", children: [_jsx("span", { children: "Need to sign in with a different account?" }), _jsx(Link, { to: "/logout", className: "text-primary font-medium", children: "Log out" })] })] }) })] }));
};
//# sourceMappingURL=MfaSetupPage.js.map